"use client"

import { UNIVERSAL_LINK_STORE_URL } from "@/_constant/constant"
import React, { Component, type ReactNode } from "react"
import ErrorPageLayout from "@/app/components/ErrorState/Error/ErrorPageLayout"
import { reportClientEvent } from "@/lib/observability/client-error-reporter"
import { nativeBr } from "@/utils/nativeBr"
import { useRouter } from "next/navigation"
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime"

type ReactErrorBoundaryProps = {
  name: string
  children: ReactNode
  fallback?: ReactNode
  router?: AppRouterInstance
}

type ReactErrorBoundaryState = {
  hasError: boolean
}

class ReactErrorBoundary extends Component<
  ReactErrorBoundaryProps,
  ReactErrorBoundaryState
> {
  state: ReactErrorBoundaryState = {
    hasError: false,
  }

  static getDerivedStateFromError(): ReactErrorBoundaryState {
    return { hasError: true }
  }

  private getIsHomeErrorLocation() {
    return typeof window !== "undefined" && window.location.pathname === "/"
  }

  handleGoHome = () => {
    if (this.getIsHomeErrorLocation()) {
      if (!nativeBr.isEnabled && this.props.router) {
        this.props.router.push(UNIVERSAL_LINK_STORE_URL)
        return
      }

      if (!nativeBr.isEnabled) {
        window.location.href = UNIVERSAL_LINK_STORE_URL
        return
      }

      void nativeBr.dismiss()
      return
    }

    this.setState({ hasError: false }, () => {
      if (this.props.router) {
        this.props.router.push("/")
        return
      }

      window.location.href = "/"
    })
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    try {
      reportClientEvent("react.render.error", {
        boundaryName: this.props.name,
        error,
        componentStack: errorInfo.componentStack,
      })
    } catch {
      // no-op: reporting must not trigger another render failure.
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <ErrorPageLayout
            isNavigation={false}
            icon="/images/illustrations/empty/graphic_empty_08.webp"
            title="오류가 발생했습니다."
            desc="홈으로 이동해 다시 이용해주세요."
            button="홈으로 이동"
            onButtonClick={this.handleGoHome}
          />
        )
      )
    }

    return this.props.children
  }
}

const ReactErrorBoundaryWithRouter = (
  props: Omit<ReactErrorBoundaryProps, "router">,
) => {
  const router = useRouter()

  return <ReactErrorBoundary {...props} router={router} />
}

export default ReactErrorBoundaryWithRouter
